/**
 * How the People list reads.
 *
 * The page's question is **who is in my life, and what do I actually know
 * about them** — a life coach that remembers your daughter's name and that she
 * is in fifth grade can say something worth hearing; one holding a bare list of
 * names cannot.
 *
 * So a row's quiet line is what is known, and the sections separate the people
 * you have told the app about from the ones it heard in conversation and is
 * still holding. Pure and import-free, for `npm run test:people`.
 */

export interface PersonRow {
  id: number | string
  title: string
  description?: string | null
  metadata?: Record<string, unknown> | null
}

const str = (v: unknown): string =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : ''

const list = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : []

/**
 * The one quiet line under a name: who they are to you, then the most
 * grounding fact we hold. Never every field — a row that lists eight things
 * is a form, not a row.
 */
export function knownLine(p: PersonRow): string {
  const m = p.metadata || {}
  const parts: string[] = []
  const rel = str(m.relationship_type)
  if (rel) parts.push(rel)

  const age = typeof m.age === 'number' ? m.age : Number(str(m.age)) || 0
  if (age > 0) parts.push(`${age}`)

  // School beats occupation for a child, occupation for an adult — whichever
  // is present is the one that grounds a sentence.
  const school = str(m.school)
  const grade = str(m.grade)
  const job = str(m.occupation)
  if (school) parts.push(grade ? `${grade} grade at ${school}` : school)
  else if (job) parts.push(job)

  if (parts.length === 0) {
    const about = str(p.description)
    if (about) return about.length > 90 ? `${about.slice(0, 87)}…` : about
    return 'Nothing known yet'
  }
  return parts.join(' · ')
}

/** True when the app holds nothing beyond a name. */
export function isBare(p: PersonRow): boolean {
  return knownLine(p) === 'Nothing known yet'
}

/** Aliases, for search and for the row's title tooltip. */
export function aliasesOf(p: PersonRow): string[] {
  return list((p.metadata || {}).aliases)
}

export function matchesQuery(p: PersonRow, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  const m = p.metadata || {}
  const hay = [
    p.title || '',
    str(p.description),
    str(m.relationship_type), str(m.occupation), str(m.school), str(m.grade),
    ...aliasesOf(p), ...list(m.interests), ...list(m.hobbies),
  ].join(' ').toLowerCase()
  return hay.includes(needle)
}

/** The summary under the page title, in words. */
export function peopleSummary(people: PersonRow[]): string {
  if (people.length === 0) return 'Nobody here yet.'
  const bare = people.filter(isBare).length
  const who = people.length === 1 ? '1 person' : `${people.length} people`
  if (bare === 0) return `${who}, and something known about each.`
  if (bare === people.length) return `${who} — just names so far.`
  return `${who} · ${bare} still just a name.`
}

/** Alphabetical, but people we know nothing about sink to the bottom. */
export function sortPeople<T extends PersonRow>(people: T[]): T[] {
  return [...people].sort((a, b) => {
    const d = Number(isBare(a)) - Number(isBare(b))
    if (d !== 0) return d
    return (a.title || '').localeCompare(b.title || '')
  })
}
